import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  HiOutlineShieldCheck, HiOutlineRefresh, HiOutlinePlus,
  HiOutlineTrash, HiOutlineEye, HiOutlineEyeOff,
  HiOutlineUpload, HiOutlineKey, HiOutlineUser,
  HiOutlineDocumentText, HiOutlineDownload, HiOutlineX
} from 'react-icons/hi';
import { getVpnDetails, addVpnDetail, deleteVpnDetail, revealVpnPassword, downloadVpnConfig } from '../services/api';
import './VpnDetails.css';

function VpnDetails({ gcpProjects, selectedProjectId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  // Add form state
  const [formName, setFormName] = useState('');
  const [formUsername, setFormUsername] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formFile, setFormFile] = useState(null);
  const [saving, setSaving] = useState(false);

  // Reveal state
  const [revealingId, setRevealingId] = useState(null);
  const [revealPassword, setRevealPassword] = useState('');
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [revealLoading, setRevealLoading] = useState(false);

  const activeProject = gcpProjects.find(p => p._id === selectedProjectId);

  const fetchEntries = useCallback(async (signal) => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const res = await getVpnDetails(activeProject._id);
      if (!signal?.cancelled) setEntries(res.data || []);
    } catch (err) {
      if (!signal?.cancelled) toast.error('Failed to load VPN details: ' + (err.response?.data?.error || err.message));
    } finally {
      if (!signal?.cancelled) setLoading(false);
    }
  }, [activeProject]);

  useEffect(() => {
    setEntries([]);
    setRevealedPasswords({});
    if (!activeProject) return;
    const signal = { cancelled: false };
    fetchEntries(signal);
    return () => { signal.cancelled = true; };
  }, [activeProject, fetchEntries]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!activeProject || !formName.trim()) return;
    setSaving(true);

    let configFileName = null;
    let configFileData = null;

    if (formFile) {
      configFileName = formFile.name;
      const reader = new FileReader();
      configFileData = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(formFile);
      });
    }

    try {
      await addVpnDetail({
        projectId: activeProject._id,
        name: formName.trim(),
        username: formUsername.trim() || null,
        password: formPassword || null,
        configFileName,
        configFileData,
      });
      toast.success('VPN details added');
      setShowAddModal(false);
      setFormName(''); setFormUsername(''); setFormPassword(''); setFormFile(null);
      fetchEntries({});
    } catch (err) {
      toast.error('Failed to add VPN details: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this VPN entry?')) return;
    try {
      await deleteVpnDetail(id);
      setEntries(prev => prev.filter(e => e._id !== id));
      setRevealedPasswords(prev => { const n = { ...prev }; delete n[id]; return n; });
      toast.success('VPN entry deleted');
    } catch (err) {
      toast.error('Failed to delete: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleReveal = async (id) => {
    if (!revealPassword) { toast.error('Enter your login password'); return; }
    setRevealLoading(true);
    try {
      const res = await revealVpnPassword(id, revealPassword);
      setRevealedPasswords(prev => ({ ...prev, [id]: res.data.password }));
      setRevealingId(null);
      setRevealPassword('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Authentication failed');
    } finally {
      setRevealLoading(false);
    }
  };

  const handleHidePassword = (id) => {
    setRevealedPasswords(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const handleDownloadConfig = async (entry) => {
    try {
      const res = await downloadVpnConfig(entry._id);
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = entry.configFileName || 'vpn-config';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download config: ' + (err.response?.data?.error || err.message));
    }
  };

  if (!activeProject) {
    return (
      <div className="vpn-details page-container">
        <div className="pv-header"><div className="pv-header-left"><h1><HiOutlineShieldCheck size={28} /> VPN Details</h1></div></div>
        <div className="empty-state"><p>Select a GCP project to view VPN details.</p></div>
      </div>
    );
  }

  return (
    <div className="vpn-details page-container">
      <div className="pv-header">
        <div className="pv-header-left">
          <h1><HiOutlineShieldCheck size={28} /> VPN Details</h1>
          <span className="pv-subtitle">{activeProject.projectName}</span>
        </div>
        <div className="pv-header-right" style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => fetchEntries({})} disabled={loading}>
            <HiOutlineRefresh size={14} className={loading ? 'spin' : ''} /> Refresh
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowAddModal(true)}>
            <HiOutlinePlus size={14} /> Add VPN Details
          </button>
        </div>
      </div>

      {loading && <div className="loading-bar"><div className="loading-bar-inner" /></div>}

      {!loading && entries.length === 0 && (
        <div className="empty-state"><p>No VPN details added yet for <strong>{activeProject.projectName}</strong>.</p></div>
      )}

      {entries.length > 0 && (
        <div className="vpn-cards">
          {entries.map(entry => (
            <div className="vpn-card card" key={entry._id}>
              <div className="vpn-card-header">
                <div className="vpn-card-title">
                  <HiOutlineShieldCheck size={18} />
                  <h3>{entry.name}</h3>
                </div>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(entry._id)}>
                  <HiOutlineTrash size={14} /> Delete
                </button>
              </div>

              <div className="vpn-card-body">
                <div className="vpn-field">
                  <span className="vpn-field-label"><HiOutlineUser size={14} /> Username</span>
                  <span className="vpn-field-value">{entry.username || '—'}</span>
                </div>

                <div className="vpn-field">
                  <span className="vpn-field-label"><HiOutlineKey size={14} /> Password</span>
                  <div className="vpn-password-area">
                    {revealedPasswords[entry._id] !== undefined ? (
                      <div className="vpn-password-revealed">
                        <code className="vpn-password-text">{revealedPasswords[entry._id]}</code>
                        <button className="btn btn-ghost btn-xs" onClick={() => handleHidePassword(entry._id)} title="Hide">
                          <HiOutlineEyeOff size={14} /> Hide
                        </button>
                      </div>
                    ) : entry.hasPassword ? (
                      <>
                        <span className="vpn-password-masked">••••••••••</span>
                        {revealingId === entry._id ? (
                          <div className="vpn-reveal-form">
                            <input
                              type="password"
                              placeholder="Enter your login password"
                              value={revealPassword}
                              onChange={(e) => setRevealPassword(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleReveal(entry._id)}
                              autoFocus
                            />
                            <button className="btn btn-primary btn-xs" onClick={() => handleReveal(entry._id)} disabled={revealLoading}>
                              {revealLoading ? 'Verifying…' : 'Confirm'}
                            </button>
                            <button className="btn btn-ghost btn-xs" onClick={() => { setRevealingId(null); setRevealPassword(''); }}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button className="btn btn-ghost btn-xs" onClick={() => setRevealingId(entry._id)} title="Show password">
                            <HiOutlineEye size={14} /> Show
                          </button>
                        )}
                      </>
                    ) : (
                      <span className="vpn-field-value">—</span>
                    )}
                  </div>
                </div>

                {entry.hasConfigFile && (
                  <div className="vpn-field">
                    <span className="vpn-field-label"><HiOutlineDocumentText size={14} /> Config File</span>
                    <button className="download-btn" onClick={() => handleDownloadConfig(entry)}>
                      <HiOutlineDownload size={14} />
                      <span>{entry.configFileName || 'Download'}</span>
                    </button>
                  </div>
                )}

                <div className="vpn-field vpn-field-meta">
                  <span>Added {new Date(entry.createdAt).toLocaleString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Add VPN Modal ── */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal vpn-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vpn-modal-header">
              <h2><HiOutlineShieldCheck size={20} /> Add VPN Details</h2>
              <button className="vpn-modal-close" onClick={() => setShowAddModal(false)} title="Close"><HiOutlineX size={18} /></button>
            </div>
            <form onSubmit={handleAdd}>
              <div className="form-group">
                <label>Name <span className="required">*</span></label>
                <input type="text" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="e.g. Production VPN" required />
              </div>
              <div className="form-group">
                <label>Username</label>
                <input type="text" value={formUsername} onChange={(e) => setFormUsername(e.target.value)} placeholder="VPN username" />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input type="password" value={formPassword} onChange={(e) => setFormPassword(e.target.value)} placeholder="VPN password (stored encrypted)" />
              </div>
              <div className="form-group">
                <label>Config File</label>
                <div className="file-upload-area">
                  <input type="file" id="vpn-config-file" onChange={(e) => setFormFile(e.target.files[0])} />
                  <label htmlFor="vpn-config-file" className="file-upload-btn">
                    <HiOutlineUpload size={16} />
                    <span>{formFile ? formFile.name : 'Choose file (.ovpn, .conf, etc.)'}</span>
                  </label>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving || !formName.trim()}>
                  {saving ? 'Saving…' : 'Add VPN Details'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default VpnDetails;
